import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, CardBody, Row, Col, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useGetStatuses } from 'api/inventory-status/service';
import Select from 'components/Select';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const SInventoryTabs = forwardRef(({
    permissions,
    activeColorStyle,
    activeColor = 'blue',
    isTheme2 = false,
    selectStyles,
    inputStyle,
    inputClassName,
    initialData,
    inventoryStatus,
}, ref) => {
    const { t } = useTranslation();
    useImperativeHandle(ref, () => ({
        getData: () => form
    }));
    const [form, setForm] = useState({
        stock_number_assignment_option: 'incrementing_number',
        stock_number_purchase_prefix: '',
        next_available_stock_number: '',
        stock_number_partial_vin: 'right',
        stock_number_partial_vin_number: 0,
        stock_number_rep_prefix: '',
        status_for_repo: null,
    });

    const { data: statusesData, isLoading: isLoadingStatuses } = useGetStatuses();

    const inputFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '14px';

    const statusOptions = useMemo(() => {
        const results = inventoryStatus || initialData?.inventory_status || statusesData?.results || [];
        return results.map((s) => ({ label: s.status_name, value: s.id }));
    }, [statusesData, initialData?.inventory_status, inventoryStatus]);

    const assignmentOptions = useMemo(() => [
        { value: 'incrementing_number', label: t('userDefinedInventoryTabs.incrementingNumber') },
        { value: 'partial_vin', label: t('userDefinedInventoryTabs.partialVin') }
    ], [t]);

    const partialVinOptions = useMemo(() => [
        { value: 'right', label: t('userDefinedInventoryTabs.right') },
        { value: 'left', label: t('userDefinedInventoryTabs.left') }
    ], [t]);

    useEffect(() => {
        if (initialData) {
            setForm(prev => ({
                ...prev,
                ...initialData,
                status_for_repo: initialData.status_for_repo?.id || initialData.status_for_repo || null
            }));
        }
    }, [initialData]);

    const renderTextInput = (inputProps) => (
        <Input
            {...inputProps}
            style={{
                ...(isTheme2 ? inputStyle : { fontSize: inputFontSize }),
                height: '38px',
                ...inputProps.style,
            }}
            className={inputClassName ? inputClassName(false) : 'mb-0 text-center'}
        />
    );

    const renderCard = (title, description, input) => (
        <Card style={{ overflow: 'hidden' }} className="mb-2 pb-0 pt-0">
            <CardBody className="py-2 px-3">
                <Row className="align-items-center">
                    <Col md="8" className="d-flex flex-column justify-content-center">
                        <h4
                            className="mb-1 font-weight-bold institution-inventory-card__title"
                            style={{ ...(isTheme2 ? {} : { fontSize: '14px', color: '#1a1a1b' }) }}
                        >
                            {title}
                        </h4>
                        <div
                            className="text-muted institution-inventory-card__description"
                            style={{ ...(isTheme2 ? { lineHeight: '1.4' } : { fontSize: '13px', lineHeight: '1.4' }) }}
                        >
                            {description}
                        </div>
                    </Col>
                    <Col md="4" className="d-flex justify-content-end align-items-center">
                        <div style={{ width: '250px' }}>
                            {input}
                        </div>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );

    return (
        <div>
            {renderCard(
                t('userDefinedInventoryTabs.stockNumberAssignmentOption') + " (SA)",
                t('userDefinedInventoryTabs.stockNumberAssignmentDescription'),
                <Select
                    activeColor={activeColor}
                    className="react-select"
                    styles={selectStyles}
                    placeholder="Select"
                    name="stock_number_assignment_option"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={assignmentOptions.find(opt => opt.value === form.stock_number_assignment_option) || null}
                    onChange={(option) => setForm({ ...form, stock_number_assignment_option: option?.value || '' })}
                    options={assignmentOptions}
                />
            )}

            {form.stock_number_assignment_option === 'partial_vin' && (
                <>
                    {renderCard(
                        t('userDefinedInventoryTabs.stockNumberPartialVin'),
                        t('userDefinedInventoryTabs.partialVinDescription'),
                        <Select
                            activeColor={activeColor}
                            className="react-select"
                            styles={selectStyles}
                            placeholder="Select"
                            name="stock_number_partial_vin"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={partialVinOptions.find(opt => opt.value === form.stock_number_partial_vin) || null}
                            onChange={(option) => setForm({ ...form, stock_number_partial_vin: option?.value || '' })}
                            options={partialVinOptions}
                        />
                    )}

                    {renderCard(
                        t('userDefinedInventoryTabs.stockNumberPartialVin'),
                        t('userDefinedInventoryTabs.partialVinNumberDescription'),
                        renderTextInput({
                            type: 'number',
                            min: 1,
                            value: form.stock_number_partial_vin_number,
                            onChange: (e) => setForm({ ...form, stock_number_partial_vin_number: e.target.value }),
                        })
                    )}
                </>
            )}

            {renderCard(
                t('userDefinedInventoryTabs.nextAvailableStockNumber'),
                t('userDefinedInventoryTabs.nextAvailableStockNumberDescription'),
                renderTextInput({
                    type: 'text',
                    value: form.next_available_stock_number,
                    onChange: (e) => setForm({ ...form, next_available_stock_number: e.target.value }),
                })
            )}

            {renderCard(
                t('userDefinedInventoryTabs.stockNumberPurchasePrefix'),
                t('userDefinedInventoryTabs.stockNumberPurchasePrefixDescription'),
                renderTextInput({
                    type: 'text',
                    value: form.stock_number_purchase_prefix,
                    onChange: (e) => setForm({ ...form, stock_number_purchase_prefix: e.target.value }),
                })
            )}

            {renderCard(
                t('userDefinedInventoryTabs.stockNumberRepoPrefix'),
                t('userDefinedInventoryTabs.stockNumberRepoPrefixDescription'),
                renderTextInput({
                    type: 'text',
                    value: form.stock_number_rep_prefix,
                    onChange: (e) => setForm({ ...form, stock_number_rep_prefix: e.target.value }),
                })
            )}

            {renderCard(
                t('userDefinedInventoryTabs.defaultRepoStatus'),
                t('userDefinedInventoryTabs.defaultRepoStatusDescription'),
                <Select
                    activeColor={activeColor}
                    className="react-select"
                    styles={selectStyles}
                    placeholder="Select Status"
                    name="status_for_repo"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={statusOptions.find(opt => opt.value === form.status_for_repo) || null}
                    onChange={(option) => setForm({ ...form, status_for_repo: option?.value || null })}
                    options={statusOptions}
                    isDisabled={isLoadingStatuses}
                />
            )}
        </div>
    );
});

export default SInventoryTabs;
